import { useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, PenTool, Clock, TrendingUp, Users, Target, Database, Lightbulb, FileText, Settings, ArrowLeft, Zap, Cog, Activity, Webhook, Calendar, CalendarDays, Globe, ShoppingCart, Shield, Package, Instagram, Cpu, MessageSquare, Smartphone, Key, Navigation, Bot, Plug, TvMinimalPlay, Send, Mail, BarChart3, Kanban, CheckCircle, Rss, Bell, Copy, Layout, LayoutDashboard, LayoutTemplate, Monitor, Brain, Contact, BadgeDollarSign, GraduationCap, Tag, CreditCard, Workflow, UserSquare, UserPlus, Sparkles, Mic, Crosshair, FolderOpen, Album, List, Link as LinkIcon, Search, FileSpreadsheet, Share2, FlaskConical, ScrollText, Code } from "lucide-react";
import { useCustomBrandFeatures } from "@/hooks/useCustomBrandFeatures";
import SuperAdminNavigation from "@/components/SuperAdminNavigation";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSettings } from "@/contexts/SettingsContext";
import { useSidebar } from "@/components/ui/sidebar";
import { useMenuTutorialVideos } from "@/hooks/useMenuTutorialVideos";
import { SettingsMenuItemWithTutorial } from "@/components/navigation/SettingsMenuItemWithTutorial";
import { useTypedTranslation } from "@/lib/i18n/hooks";

export function SettingsSidebar() {
  const { setIsSettingsSidebarOpen } = useSettings();
  const { setOpenMobile } = useSidebar();
  const location = useLocation();
  const { hasFeature } = useCustomBrandFeatures();
  const { isSuperAdmin, isLoading: superAdminLoading } = useSuperAdmin();
  const { settingsVideos } = useMenuTutorialVideos();
  const { tNav } = useTypedTranslation();
  
  // State for collapsible sections - admin expanded by default so Module Descriptions / Sales Code Indexer are visible
  const [openSections, setOpenSections] = useState({
    logsAudits: false,
    emailServices: false,
    admin: true,
    superAdmin: false,
    scale2y3x: false,
    businessMetrics: false,
    articles: false,
    realEstate360: true,
    customApps: false,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const [searchQuery, setSearchQuery] = useState("");
  const normalizedQuery = searchQuery.trim().toLowerCase();

  // Section titles for search (section visible if title or any item title matches)
  const sectionMeta: { key: keyof typeof openSections; title: string }[] = useMemo(() => [
    { key: "logsAudits", title: "Logs & Audits" },
    { key: "emailServices", title: "Email Services" },
    { key: "admin", title: "Admin" },
    { key: "superAdmin", title: "Super Admin" },
    { key: "scale2y3x", title: "Scale at Speed: 2Y3X" },
    { key: "businessMetrics", title: "Business Metrics" },
    { key: "realEstate360", title: "360 Real Estate" },
    { key: "customApps", title: "Custom Apps" },
  ], []);

  // 360 Real Estate settings items
  const realEstate360SettingsItems = [
    { title: "Log", icon: ScrollText, path: "/360/log" },
    { title: "Settings", icon: Settings, path: "/360/settings" },
  ];

  // Logs & Audits settings items
  const logsAuditsSettingsItems = [
    {
      title: "Activity Log",
      icon: Clock,
      path: "/activity",
    },
    {
      title: "API Calls Audit",
      icon: Activity,
      path: "/api-calls-audit",
    },
    {
      title: "Job Reporting Dashboard",
      icon: Activity,
      path: "/job-reporting",
    },
    {
      title: "Incoming Event Log",
      icon: FileText,
      path: "/incoming-event-log",
    },
  ];

  const customAppsSettingsItems = [
    {
      title: "Manage Custom Apps",
      icon: Plug,
      path: "/custom-apps",
    },
  ];

  // Admin settings items (Module Descriptions and Sales Code Indexer at top for visibility)
  const adminSettingsItems = [
    {
      title: "Module Descriptions",
      icon: FileText,
      path: "/admin/module-descriptions",
    },
    {
      title: "Code Indexer",
      icon: Code,
      path: "/admin/code-index",
    },
    {
      title: "Technical Readiness",
      icon: Activity,
      path: "/settings/technical-readiness",
    },
    {
      title: "Menu Tutorial Videos",
      icon: TvMinimalPlay,
      path: "/admin/menu-tutorial-videos",
    },
    {
      title: "Project Progress Config",
      icon: Settings,
      path: "/project-progress-configurator",
    },
    {
      title: "Email Designer",
      icon: Mail,
      path: "/email-designer",
    },
    {
      title: "Experiments",
      icon: FlaskConical,
      path: "/experiments",
    },
    {
      title: "Page Builder",
      icon: Monitor,
      path: "/experiments/document-to-page",
    },
    {
      title: "Data Orchestrator",
      icon: Workflow,
      path: "/experiments/data-orchestrator",
    },
    {
      title: "Image Coordinates",
      icon: Target,
      path: "/experiments/image-coordinates",
    },
    {
      title: "Collaborative Editor",
      icon: Users,
      path: "/experiments/collaborative-editor",
    },
    {
      title: "Dashboard Components",
      icon: LayoutDashboard,
      path: "/holdco-views/settings/components",
    },
    {
      title: "Push Notifications",
      icon: Bell,
      path: "/push-notifications",
    },
    {
      title: "Clone Prompts",
      icon: Copy,
      path: "/clone-prompts",
    },
    {
      title: "Clone Menu Tutorial Videos",
      icon: Copy,
      path: "/clone-growthos-courses",
    },
    {
      title: "Services Provisioned",
      icon: Package,
      path: "/services-provisioned",
    },
    {
      title: "Apify Usage Monitor",
      icon: TrendingUp,
      path: "/admin/apify-usage",
    },
    {
      title: "Menu Metadata Manager",
      icon: Globe,
      path: "/admin/route-metadata",
    },
    {
      title: "Configure Mobile Footer",
      icon: Smartphone,
      path: "/mobile-footer-config",
    },
    {
      title: "LLM Source Configuration",
      icon: Brain,
      path: "/admin/llm-config",
    },
    {
      title: "User Activity Tracking",
      icon: Activity,
      path: "/admin/user-activity-tracking",
    },
    {
      title: "User Time Tracking",
      icon: Clock,
      path: "/admin/user-time-tracking",
    },
    {
      title: "Growth Standup Admin",
      icon: Calendar,
      path: "/admin/growth-standup",
    },
    {
      title: "Team Cards",
      icon: UserSquare,
      path: "/admin/team-cards",
    },
    {
      title: "Accounting API Tokens",
      icon: Key,
      path: "/accounting-api-tokens",
    },
    {
      title: "Voice Assistant Tools",
      icon: Mic,
      path: "/admin/voice-assistant-tools",
    },
    {
      title: "Support Portal Articles",
      icon: FileText,
      path: "/admin/support-portal/articles",
    },
    {
      title: "Support Portal Categories",
      icon: FolderOpen,
      path: "/admin/support-portal/categories",
    },
  ];

  // Scale at Speed: 2Y3X settings items
  const scale2y3xSettingsItems = [
    {
      title: "Phases",
      icon: Kanban,
      path: "/scale-2y3x/phases",
    },
    {
      title: "Quarterly Sprints",
      icon: CalendarDays,
      path: "/scale-2y3x/sprints",
    },
  ];

  // Business Metrics settings items
  const businessMetricsSettingsItems = [
    {
      title: "Metrics Cockpit",
      icon: Activity,
      path: "/admin/metrics-cockpit",
    },
    {
      title: "Metrics Aggregator",
      icon: TrendingUp,
      path: "/metrics-aggregations",
    },
    {
      title: "Analytics Engine",
      icon: Activity,
      path: "/analytics-engine",
    },
    {
      title: "Business Events Settings",
      icon: Settings,
      path: "/business-metrics-settings",
    },
    {
      title: "Business Event Webhook",
      icon: Webhook,
      path: "/business-event-webhook",
    },
  ];

  type SettingsCategory = "Marketing" | "Sales" | "Operations" | "Admin" | "Systems";
  const sectionToItems: Partial<Record<keyof typeof openSections, { title: string; path: string; icon: typeof Globe; featureKey?: string }[]>> = {
    logsAudits: logsAuditsSettingsItems,
    admin: adminSettingsItems,
    scale2y3x: scale2y3xSettingsItems,
    businessMetrics: businessMetricsSettingsItems,
    realEstate360: realEstate360SettingsItems,
    customApps: customAppsSettingsItems,
  };
  const inlineSectionSearchTitles: Partial<Record<keyof typeof openSections, string[]>> = {
    emailServices: ["Email Providers"],
  };
  const ORDERED_SECTIONS: (keyof typeof openSections)[] = [
    "emailServices", "customApps", "articles",
    "businessMetrics",
    "logsAudits", "admin", "superAdmin",
    "scale2y3x", "realEstate360",
  ];
  const sectionToCategory: Record<keyof typeof openSections, SettingsCategory> = {
    emailServices: "Marketing", customApps: "Systems", articles: "Marketing",
    businessMetrics: "Operations",
    logsAudits: "Admin", admin: "Admin", superAdmin: "Admin",
    scale2y3x: "Systems", realEstate360: "Systems",
  };
  const sectionSearchIndex = useMemo(() => {
    const idx: Record<string, string[]> = {};
    for (const m of sectionMeta) {
      const items = sectionToItems[m.key];
      const inlineTitles = inlineSectionSearchTitles[m.key];
      if (inlineTitles !== undefined) {
        idx[m.key] = [m.title, ...inlineTitles];
      } else {
        idx[m.key] = items ? [m.title, ...items.map((i) => i.title)] : [m.title];
      }
    }
    return idx;
  }, [sectionMeta]);
  const returnTrue = useCallback((_key?: keyof typeof openSections) => true, []);
  const sectionVisible = useMemo(() => {
    if (!normalizedQuery) return returnTrue;
    return (key: keyof typeof openSections) => {
      const titles = sectionSearchIndex[key];
      return titles?.some((t) => t.toLowerCase().includes(normalizedQuery)) ?? false;
    };
  }, [normalizedQuery, sectionSearchIndex, returnTrue]);
  const firstVisibleSectionInCategory = useMemo(() => {
    const first: Partial<Record<SettingsCategory, keyof typeof openSections>> = {};
    for (const key of ORDERED_SECTIONS) {
      const cat = sectionToCategory[key];
      if (sectionVisible(key) && first[cat] === undefined) first[cat] = key;
    }
    return first;
  }, [normalizedQuery, sectionVisible]);
  const isFirstVisibleInCategory = (key: keyof typeof openSections) =>
    firstVisibleSectionInCategory[sectionToCategory[key]] === key;
  const sectionOrder: Record<keyof typeof openSections, number> = useMemo(() => {
    const o: Record<string, number> = {};
    ORDERED_SECTIONS.forEach((key, i) => { o[key] = i; });
    return o as Record<keyof typeof openSections, number>;
  }, []);
  const filterItemsBySearch = useCallback(<T extends { title: string }>(items: T[]): T[] => {
    if (!normalizedQuery) return items;
    return items.filter((item) => item.title.toLowerCase().includes(normalizedQuery));
  }, [normalizedQuery]);
  const itemMatchesSearch = useCallback(
    (itemTitle: string) => !normalizedQuery || itemTitle.toLowerCase().includes(normalizedQuery),
    [normalizedQuery]
  );
  const sectionHasVisibleItems = useCallback(
    (key: keyof typeof openSections) => {
      if (!normalizedQuery) return true;
      // If section title matches the search, show all items
      const sectionTitle = sectionMeta.find(m => m.key === key)?.title?.toLowerCase() || '';
      if (sectionTitle.includes(normalizedQuery)) return true;
      const items = sectionToItems[key];
      if (!items) return true;
      return filterItemsBySearch(items).length > 0;
    },
    [normalizedQuery, filterItemsBySearch, sectionMeta]
  );
  const CategorySeparator = ({ label }: { label: string }) => (
    <div className="flex items-center gap-2 px-2 py-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-[12px] shrink-0 flex-1 h-px bg-muted-foreground/50" />
    </div>
  );

  const handleMobileOpenChange = (open: boolean) => {
    setOpenMobile(open);
    if (!open) setIsSettingsSidebarOpen(false);
  };

  return (
    <Sidebar
      mobileSheetContentClassName="!bg-secondary/80 [&>div]:!bg-secondary/80"
      className="h-full w-full border-0 [&>div>div]:bg-secondary/80 [&_[data-active=true]]:!bg-background [&_[data-active=true]]:!text-primary [&_[data-active=true]]:font-semibold [&_[data-active=true]]:shadow-sm [&_[data-active=true]]:ring-1 [&_[data-active=true]]:ring-primary/20">
      <SidebarHeader className="!p-0 !pt-0">
        <div className="px-3 pb-3 pt-0 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Settings</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSettingsSidebarOpen(false)}
              className="h-8 w-8 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 bg-background"
            />
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="gap-0 flex flex-col">
        {sectionVisible("logsAudits") && sectionHasVisibleItems("logsAudits") && (
        <div style={{ order: sectionOrder.logsAudits }}>
        {isFirstVisibleInCategory("logsAudits") && <CategorySeparator label={sectionToCategory.logsAudits} />}
        <Collapsible open={openSections.logsAudits} onOpenChange={() => toggleSection('logsAudits')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.logsAudits ? 'bg-accent text-accent-foreground' : ''}`}>
                Logs & Audits
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.logsAudits ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(logsAuditsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("realEstate360") && sectionHasVisibleItems("realEstate360") && (
        <div style={{ order: sectionOrder.realEstate360 }}>
        {isFirstVisibleInCategory("realEstate360") && <CategorySeparator label={sectionToCategory.realEstate360} />}
        <Collapsible open={openSections.realEstate360} onOpenChange={() => toggleSection('realEstate360')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.realEstate360 ? 'bg-accent text-accent-foreground' : ''}`}>
                360 Real Estate
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.realEstate360 ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(realEstate360SettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("emailServices") && sectionHasVisibleItems("emailServices") && (
        <div style={{ order: sectionOrder.emailServices }}>
        {isFirstVisibleInCategory("emailServices") && <CategorySeparator label={sectionToCategory.emailServices} />}
        <Collapsible open={openSections.emailServices} onOpenChange={() => toggleSection('emailServices')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.emailServices ? 'bg-accent text-accent-foreground' : ''}`}>
                Email Services
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.emailServices ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Email Providers") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/email-providers'}
                      tooltip="Email Providers"
                    >
                      <Link to="/email-providers" className="w-full">
                        <Mail />
                        <span>Email Providers</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("customApps") && sectionHasVisibleItems("customApps") && (
        <div style={{ order: sectionOrder.customApps }}>
        {isFirstVisibleInCategory("customApps") && <CategorySeparator label={sectionToCategory.customApps} />}
        <Collapsible open={openSections.customApps} onOpenChange={() => toggleSection('customApps')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.customApps ? 'bg-accent text-accent-foreground' : ''}`}>
                Custom Apps
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.customApps ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(customAppsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("scale2y3x") && sectionHasVisibleItems("scale2y3x") && (
        <div style={{ order: sectionOrder.scale2y3x }}>
        {isFirstVisibleInCategory("scale2y3x") && <CategorySeparator label={sectionToCategory.scale2y3x} />}
        <Collapsible open={openSections.scale2y3x} onOpenChange={() => toggleSection('scale2y3x')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.scale2y3x ? 'bg-accent text-accent-foreground' : ''}`}>
                Scale at Speed: 2Y3X
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.scale2y3x ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(scale2y3xSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("businessMetrics") && sectionHasVisibleItems("businessMetrics") && (
        <div style={{ order: sectionOrder.businessMetrics }}>
        {isFirstVisibleInCategory("businessMetrics") && <CategorySeparator label={sectionToCategory.businessMetrics} />}
        <Collapsible open={openSections.businessMetrics} onOpenChange={() => toggleSection('businessMetrics')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.businessMetrics ? 'bg-accent text-accent-foreground' : ''}`}>
                Business Metrics
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.businessMetrics ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(businessMetricsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("admin") && sectionHasVisibleItems("admin") && (
        <div style={{ order: sectionOrder.admin }}>
        {isFirstVisibleInCategory("admin") && <CategorySeparator label={sectionToCategory.admin} />}
        <Collapsible open={openSections.admin} onOpenChange={() => toggleSection('admin')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.admin ? 'bg-accent text-accent-foreground' : ''}`}>
                Admin
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.admin ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(adminSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("superAdmin") && sectionHasVisibleItems("superAdmin") && (
        <div style={{ order: sectionOrder.superAdmin }}>
        {isFirstVisibleInCategory("superAdmin") && <CategorySeparator label={sectionToCategory.superAdmin} />}
        <Collapsible open={openSections.superAdmin} onOpenChange={() => toggleSection('superAdmin')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium text-red-600 dark:text-red-400">
                Super Admin
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.superAdmin ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SuperAdminNavigation isSuperAdmin={isSuperAdmin} isLoading={superAdminLoading} />
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}